import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { useKitCopy } from "./use-kit-copy";
import { MOD_ENTER, shortcutLabel, useTaskShortcuts, type TaskShortcut } from "./use-task-shortcuts";

/**
 * One action of the bottom bar.
 *
 * - `tone`: "primary" (the main step: send, accept), "danger" (reject),
 *   "neutral" (previous, skip, pause). Neutral actions shrink to their icon
 *   below `sm`; the others share the remaining width. Neutral actions listed
 *   before the first strong one sit on the left, the rest on the right.
 * - `shortcut`: a KeyboardEvent.key (" ", "Enter", "ArrowLeft", "v"...). The
 *   bar binds it and shows it as a key hint on large screens.
 * - `loading`: spinner in place of the icon; the button is disabled.
 */
export interface TaskAction {
  key: string;
  label: string;
  icon?: IconName;
  onClick: () => void;
  tone?: "primary" | "danger" | "neutral";
  disabled?: boolean;
  loading?: boolean;
  shortcut?: string;
  testId?: string;
}

const toneClass = {
  primary: "border-primary bg-primary text-primary-foreground hover:bg-primary/90",
  danger: "border-danger/30 bg-danger/[0.08] text-danger hover:border-danger/50 hover:bg-danger/[0.12]",
  neutral: "border-border/60 bg-surface text-foreground hover:border-brand/40",
} as const;

/**
 * The fixed bar at the bottom of a task page with its 2 to 4 main actions,
 * in big buttons (48px) with their keyboard shortcuts. Put it in the
 * `actionBar` slot of TaskShell.
 *
 * ```tsx
 * <TaskActionBar
 *   actions={[
 *     { key: "prev", label: "Précédent", icon: "arrowLeft", tone: "neutral", onClick: previous, shortcut: "ArrowLeft" },
 *     { key: "send", label: "Envoyer", icon: "send", tone: "primary", onClick: send, shortcut: "Enter", loading: isUploading },
 *   ]}
 *   extraShortcuts={[{ key: " ", run: togglePlay }]}
 * />
 * ```
 *
 * - `extraShortcuts`: keys bound without a button (play/pause...).
 * - `hints`: what those extra keys do, shown on large screens.
 * - `shortcutsEnabled`: false to unbind every key (e.g. while a form is open).
 */
export function TaskActionBar({
  actions,
  extraShortcuts = [],
  hints = [],
  shortcutsEnabled = true,
  className,
}: {
  actions: TaskAction[];
  extraShortcuts?: TaskShortcut[];
  hints?: Array<{ key: string; label: string }>;
  shortcutsEnabled?: boolean;
  className?: string;
}) {
  const { copy } = useKitCopy();
  const bound: TaskShortcut[] = [
    ...actions
      .filter((action) => action.shortcut)
      .map((action) => ({ key: action.shortcut as string, run: action.onClick, enabled: !action.disabled && !action.loading })),
    ...extraShortcuts,
  ];
  useTaskShortcuts(bound, shortcutsEnabled);

  // Neutral actions before the first strong one sit on the left (previous);
  // everything from the first strong action on is grouped on the right.
  const firstStrong = actions.findIndex((action) => (action.tone ?? "neutral") !== "neutral");
  const leading = firstStrong === -1 ? actions : actions.slice(0, firstStrong);
  const trailing = firstStrong === -1 ? [] : actions.slice(firstStrong);

  const button = (action: TaskAction) => {
    const tone = action.tone ?? "neutral";
    const iconOnly = tone === "neutral";
    return (
      <button
        key={action.key}
        type="button"
        onClick={action.onClick}
        disabled={action.disabled || action.loading}
        data-testid={action.testId}
        aria-keyshortcuts={action.shortcut === " " ? "Space" : action.shortcut === MOD_ENTER ? "Control+Enter Meta+Enter" : action.shortcut}
        className={cn(
          "inline-flex h-12 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-45 sm:px-4",
          toneClass[tone],
          iconOnly ? "w-12 shrink-0 sm:w-auto" : "min-w-0 flex-1 sm:min-w-36 sm:flex-none",
        )}
      >
        {action.loading ? (
          <Icon name="loader2" className="size-4 shrink-0 animate-spin" />
        ) : action.icon ? (
          <Icon name={action.icon} className="size-4 shrink-0" />
        ) : null}
        <span className={cn("truncate", iconOnly && "sr-only sm:not-sr-only")}>{action.label}</span>
        {action.shortcut ? (
          <kbd
            aria-hidden="true"
            className={cn(
              "hidden rounded border px-1.5 py-0.5 font-sans text-[11px] font-medium lg:inline",
              tone === "primary" ? "border-primary-foreground/30 text-primary-foreground/80" : "border-border/60 text-faint-foreground",
            )}
          >
            {shortcutLabel(action.shortcut, copy)}
          </kbd>
        ) : null}
      </button>
    );
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-2">
        {leading.map(button)}
        {trailing.length > 0 ? <div className="flex min-w-0 flex-1 items-center gap-2 sm:justify-end">{trailing.map(button)}</div> : null}
      </div>
      {hints.length > 0 ? (
        <p className="hidden items-center gap-3 text-xs text-faint-foreground lg:flex">
          <span>{copy.shortcuts}</span>
          {hints.map((hint) => (
            <span key={hint.key} className="inline-flex items-center gap-1.5">
              <kbd className="rounded border border-border/60 px-1.5 py-0.5 font-sans text-[11px]">{shortcutLabel(hint.key, copy)}</kbd>
              {hint.label}
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}
